import { getPayerDisplay } from "@/billing/services/open-statements-payer-display";
import {
  OpenStatementsRow,
  OpenStatementsSearchOption,
  PayerDisplayCache,
} from "@/billing/types/open-statements";

/** Case- and whitespace-insensitive substring match, the same shape both search options use. */
function matches(value: string, query: string): boolean {
  return value.trim().toLowerCase().includes(query);
}

/** A query with nothing but whitespace in it filters nothing out. */
export function normalizeSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

/**
 * Narrows the row set to those matching the query.
 *
 * Client searches the name the loader already resolved from the subject Patient.
 *
 * Payer searches the display resolved from `Invoice.recipient` (see
 * `useResolvedReferenceDisplays`). Rows whose recipient has not been resolved yet, or could not be
 * read, match nothing rather than matching everything, so a half-resolved cache never shows rows
 * the query does not cover.
 *
 * An empty query returns the rows untouched, so the caller can filter unconditionally.
 */
export function filterOpenStatementsTableRows(
  rows: OpenStatementsRow[],
  query: string,
  searchOption: OpenStatementsSearchOption,
  payerDisplays: PayerDisplayCache,
): OpenStatementsRow[] {
  const normalized = normalizeSearchQuery(query);
  if (!normalized) return rows;

  if (searchOption === OpenStatementsSearchOption.Payer) {
    return rows.filter((row) => matches(getPayerDisplay(row, payerDisplays), normalized));
  }

  return rows.filter((row) => matches(row.clientName, normalized));
}
